// Compatibility check utility for cross-platform support
import { getDeviceInfo, getPlatformName, getBrowserName } from './deviceDetection';
import logger from './logger';

export interface CompatibilityReport {
  isSupported: boolean;
  warnings: string[];
  errors: string[];
  deviceInfo: {
    platform: string;
    browser: string;
    isMobile: boolean;
    isTablet: boolean;
  };
  features: {
    webrtc: boolean;
    mediaDevices: boolean;
    audioContext: boolean;
    serviceWorker: boolean;
    webgl: boolean;
    indexedDB: boolean;
    notifications: boolean;
    clipboard: boolean;
  };
}

export const checkCompatibility = (): CompatibilityReport => {
  const device = getDeviceInfo();
  const warnings: string[] = [];
  const errors: string[] = [];

  // Check WebRTC support
  const hasWebRTC = !!(
    navigator.mediaDevices &&
    'getUserMedia' in navigator.mediaDevices &&
    window.RTCPeerConnection
  );

  if (!hasWebRTC) {
    errors.push('WebRTC');
  }

  // Check MediaDevices API
  const hasMediaDevices = !!(navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices);
  if (!hasMediaDevices) {
    errors.push('MediaDevices');
  }

  // Check AudioContext
  const hasAudioContext = !!('AudioContext' in window || 'webkitAudioContext' in window);
  if (!hasAudioContext) {
    warnings.push('AudioContext');
  }

  // Check Service Worker
  const hasServiceWorker = 'serviceWorker' in navigator;
  if (!hasServiceWorker) {
    warnings.push('ServiceWorker');
  }

  // Check for IndexedDB (useful for offline features)
  if (!window.indexedDB) {
    warnings.push('IndexedDB not available - Offline features limited');
  }

  // Check for Clipboard API (modern browsers should have this)
  if (!navigator.clipboard) {
    warnings.push('Clipboard API not available');
  }

  // Check for Notifications API
  if (!('Notification' in window)) {
    warnings.push('Notifications not supported');
  }

  // Check HTTPS (required for WebRTC except localhost)
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'http:' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    errors.push('HTTPS');
  }

  // Check screen size
  if (device.screenWidth < 320 || device.screenHeight < 480) {
    warnings.push('ScreenSize');
  }

  // Check for low-end device
  const cores = navigator.hardwareConcurrency || 2;
  if (cores < 2 && device.pixelRatio < 1.5) {
    warnings.push('Performance');
  }

  // Check for touch support on desktop
  if (!device.isMobile && !device.isTablet && device.hasTouch) {
    warnings.push('TouchDesktop');
  }

  const isSupported = errors.length === 0;

  return {
    isSupported,
    warnings,
    errors,
    deviceInfo: {
      platform: getPlatformName(),
      browser: getBrowserName(),
      isMobile: device.isMobile,
      isTablet: device.isTablet,
    },
    features: {
      webrtc: hasWebRTC,
      mediaDevices: hasMediaDevices,
      audioContext: hasAudioContext,
      serviceWorker: hasServiceWorker,
      webgl: device.supportsWebGL,
      indexedDB: !!window.indexedDB,
      notifications: 'Notification' in window,
      clipboard: !!navigator.clipboard,
    },
  };
};

export const logCompatibilityReport = (lang: 'fr' | 'en' = 'fr', t?: (lang: 'fr' | 'en', key: string) => string) => {
  const report = checkCompatibility();

  const errorMap: Record<string, string> = {
    'WebRTC': 'compat_webrtc',
    'MediaDevices': 'compat_mediadevices',
    'HTTPS': 'compat_https'
  };

  const warningMap: Record<string, string> = {
    'AudioContext': 'compat_audiocontext',
    'ServiceWorker': 'compat_serviceworker',
    'ScreenSize': 'compat_screensize',
    'Performance': 'compat_performance',
    'TouchDesktop': 'compat_touchdesktop'
  };

  logger.log('🔍 Compatibility Check');
  logger.log('Platform:', report.deviceInfo.platform);
  logger.log('Browser:', report.deviceInfo.browser);
  logger.log('Device Type:', report.deviceInfo.isMobile ? 'Mobile' : report.deviceInfo.isTablet ? 'Tablet' : 'Desktop');
  logger.log('Supported:', report.isSupported ? '✅' : '❌');

  if (report.errors.length > 0) {
    logger.log('❌ Errors');
    report.errors.forEach(error => {
      const msg = t ? t(lang, errorMap[error] || error) : error;
      logger.error(msg);
    });
  }

  if (report.warnings.length > 0) {
    logger.log('⚠️ Warnings');
    report.warnings.forEach(warning => {
      const msg = t ? t(lang, warningMap[warning] || warning) : warning;
      logger.warn(msg);
    });
  }

  logger.log('Features');
  logger.log('WebRTC:', report.features.webrtc ? '✅' : '❌');
  logger.log('Media Devices:', report.features.mediaDevices ? '✅' : '❌');
  logger.log('Audio Context:', report.features.audioContext ? '✅' : '❌');
  logger.log('Service Worker:', report.features.serviceWorker ? '✅' : '❌');
  logger.log('WebGL:', report.features.webgl ? '✅' : '❌');
  logger.log('IndexedDB:', report.features.indexedDB ? '✅' : '❌');
  logger.log('Notifications:', report.features.notifications ? '✅' : '❌');
  logger.log('Clipboard:', report.features.clipboard ? '✅' : '❌');

  return report;
};

export const getUnsupportedMessage = (report: CompatibilityReport, lang: 'fr' | 'en' = 'fr', t: (lang: 'fr' | 'en', key: string) => string): string => {
  if (report.isSupported) {
    return '';
  }

  const errorMap: Record<string, string> = {
    'WebRTC': 'compat_webrtc',
    'MediaDevices': 'compat_mediadevices',
    'HTTPS': 'compat_https'
  };

  const translatedErrors = report.errors.map(err => t(lang, errorMap[err] || err));

  const messages = [
    lang === 'fr' ? 'Votre navigateur ou appareil n\'est pas entièrement pris en charge.' : 'Your browser or device is not fully supported.',
    '',
    ...translatedErrors,
    '',
    lang === 'fr' ? 'Veuillez essayer :' : 'Please try:',
    lang === 'fr' ? '• Utiliser un navigateur moderne (Chrome, Firefox, Safari, Edge)' : '• Using a modern browser (Chrome, Firefox, Safari, Edge)',
    lang === 'fr' ? '• Mettre à jour votre navigateur vers la dernière version' : '• Updating your browser to the latest version',
    lang === 'fr' ? '• Accéder au site via HTTPS' : '• Accessing the site via HTTPS',
    lang === 'fr' ? '• Utiliser un autre appareil' : '• Using a different device',
  ];

  return messages.join('\n');
};

export const shouldShowCompatibilityWarning = (): boolean => {
  const report = checkCompatibility();
  return !report.isSupported || report.warnings.length > 0;
};

export const getRecommendedBrowser = (lang: 'fr' | 'en' = 'fr'): string => {
  const device = getDeviceInfo();

  if (device.isIOS) {
    return lang === 'fr' ? 'Safari ou Chrome pour iOS' : 'Safari or Chrome for iOS';
  }

  if (device.isAndroid) {
    return lang === 'fr' ? 'Chrome pour Android' : 'Chrome for Android';
  }

  if (device.isMac) {
    return lang === 'fr' ? 'Safari ou Chrome' : 'Safari or Chrome';
  }

  if (device.isWindows) {
    return lang === 'fr' ? 'Chrome ou Edge' : 'Chrome or Edge';
  }

  return lang === 'fr' ? 'Chrome, Firefox, Safari ou Edge' : 'Chrome, Firefox, Safari, or Edge';
};
